import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { insights } from "../../data/content";
import { api, mediaUrl } from "../../lib/api";
import { Eyebrow, GoldButton, ArrowLink, EASE } from "./motion";

const tabs = ["News", "Blog", "Events", "Partnerships"];

export default function Insights() {
  const [active, setActive] = useState("News");
  const [remote, setRemote] = useState(null);
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    api
      .insights()
      .then((r) => setRemote(r.data))
      .catch(() => setRemote([]));
  }, []);

  const remoteForTab = remote ? remote.filter((p) => p.category === active) : [];
  const items = remoteForTab.length > 0 ? remoteForTab : insights[active];

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.subscribe(email);
      setDone(true);
      toast.success("You're on the list.");
    } catch {
      toast.error("Could not subscribe — please try again.");
    }
  };

  return (
    <>
      <section className="bg-white px-6 py-24 lg:px-12 lg:py-[140px]" aria-label="Insights">
        <Eyebrow>Insights</Eyebrow>
        <h2 className="mt-5 max-w-2xl text-4xl font-extrabold tracking-tighter text-ink sm:text-5xl">
          The latest from across the group.
        </h2>

        <div className="mt-12 flex flex-wrap items-end justify-between gap-4 border-b border-ink/10">
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="Insights categories">
            {tabs.map((t) => (
              <button
                key={t}
                type="button"
                role="tab"
                aria-selected={active === t}
                data-testid={`insights-tab-${t.toLowerCase()}`}
                onClick={() => setActive(t)}
                className={`-mb-px border-b-2 px-4 py-4 text-[11px] font-bold uppercase tracking-[0.2em] transition-colors duration-300 ${
                  active === t ? "border-gold text-ink" : "border-transparent text-smoke hover:text-ink"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
          <div className="pb-3">
            <ArrowLink to="/insights" testId="insights-view-all">
              All Insights
            </ArrowLink>
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="mt-12 grid gap-10 md:grid-cols-3"
          >
            {items.map((a) => {
              const card = (
                <>
                  {a.image && (
                    <div className="overflow-hidden">
                      <img
                        src={mediaUrl(a.image)}
                        alt=""
                        loading="lazy"
                        className="aspect-[16/10] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                      />
                    </div>
                  )}
                  <p className="mt-5 text-[10px] font-bold uppercase tracking-[0.22em] text-gold">{a.tag}</p>
                  <h3 className="mt-2 text-lg font-bold leading-snug tracking-tight text-ink">{a.title}</h3>
                  <p className="mt-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-smoke">{a.date}</p>
                </>
              );
              return a.link ? (
                <a
                  key={a.id || a.title}
                  href={a.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block"
                  data-testid="insight-card"
                >
                  {card}
                  <span className="mt-2 inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-smoke transition-colors group-hover:text-gold">
                    Read more <ArrowUpRight className="h-3.5 w-3.5 text-gold" />
                  </span>
                </a>
              ) : a.id ? (
                <Link key={a.id} to={`/insights/${a.id}`} className="group block" data-testid="insight-card">
                  {card}
                  <span className="mt-2 inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-smoke transition-colors group-hover:text-gold">
                    Read article <ArrowUpRight className="h-3.5 w-3.5 text-gold" />
                  </span>
                </Link>
              ) : (
                <article key={a.id || a.title} className="group" data-testid="insight-card">
                  {card}
                </article>
              );
            })}
          </motion.div>
        </AnimatePresence>
      </section>

      <section className="bg-ink px-6 py-16 lg:px-12 lg:py-20" aria-label="Newsletter signup">
        <div className="flex flex-wrap items-center justify-between gap-8">
          <h3 className="max-w-md text-2xl font-bold tracking-tight text-white">
            Stay updated with the latest from Hapo Group
          </h3>
          {done ? (
            <p data-testid="newsletter-success" className="text-sm font-semibold text-gold">
              Thank you — you're subscribed.
            </p>
          ) : (
            <form onSubmit={submit} className="flex w-full max-w-md items-end gap-4" data-testid="newsletter-form">
              <label className="w-full">
                <span className="sr-only">Email address</span>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email address"
                  data-testid="newsletter-email-input"
                  className="w-full border-b border-white/25 bg-transparent py-3 text-sm text-white placeholder:text-smoke focus:border-gold focus:outline-none"
                />
              </label>
              <GoldButton type="submit" testId="newsletter-signup-button">
                Sign Up
              </GoldButton>
            </form>
          )}
        </div>
      </section>
    </>
  );
}
